import React, { useState, useCallback } from 'react';
import { FlowNode } from '../../../types/flowBuilder';
import { 
  XMarkIcon,
  CogIcon,
  InformationCircleIcon,
  PlayIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import NodeConfigRouter from './NodeConfigRouter';
import { commonStyles } from './shared/ConfigFormHelpers';

interface NodePropertiesPanelProps {
  node: FlowNode;
  onUpdateNode: (nodeId: string, updates: Partial<FlowNode['data']>) => void;
  onDeleteNode?: (nodeId: string) => void;
  onClose: () => void;
}

const NodePropertiesPanel: React.FC<NodePropertiesPanelProps> = ({
  node,
  onUpdateNode,
  onDeleteNode,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'config' | 'data' | 'info'>('config');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Node tulajdonságok frissítése
  const updateNodeProperty = useCallback((property: string, value: any) => {
    onUpdateNode(node.id, {
      ...node.data,
      [property]: value,
    });
  }, [node, onUpdateNode]);

  // Node törlése
  const handleDeleteNode = useCallback(() => {
    if (onDeleteNode) {
      onDeleteNode(node.id);
      onClose(); // Panel bezárása törlés után
    }
  }, [node.id, onDeleteNode, onClose]);

  // Config frissítése
  const updateConfig = useCallback((key: string, value: any) => {
    const newConfig = {
      ...node.data.config,
      [key]: value,
    };
    onUpdateNode(node.id, {
      ...node.data,
      config: newConfig,
    });
  }, [node, onUpdateNode]);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <CogIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Node beállítások
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {node.type} • {node.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mt-3">
          {[
            { id: 'config', label: 'Konfiguráció', icon: CogIcon },
            { id: 'data', label: 'Adatok', icon: PlayIcon },
            { id: 'info', label: 'Információ', icon: InformationCircleIcon }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-3 h-3" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {activeTab === 'config' && (
            <div className="space-y-4">
              {/* Alapvető tulajdonságok */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
                <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                  Alapbeállítások
                </h4>
                
                <div>
                  <label className={commonStyles.label}>
                    Node név
                  </label>
                  <input
                    type="text"
                    value={node.data.label || ''}
                    onChange={(e) => updateNodeProperty('label', e.target.value)}
                    className={commonStyles.input}
                    placeholder="Node neve"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Ez a név jelenik meg a node-on a canvas-en
                  </p>
                </div>
                
                <div>
                  <label className={commonStyles.label}>
                    Leírás
                  </label>
                  <textarea
                    value={node.data.description || ''}
                    onChange={(e) => updateNodeProperty('description', e.target.value)}
                    rows={2}
                    className={commonStyles.textarea}
                    placeholder="Node leírása..."
                  />
                </div>
              </div>

              {/* Node-specifikus konfiguráció */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
                <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wide">
                  Node konfiguráció
                </h4>
                <NodeConfigRouter node={node} updateConfig={updateConfig} />
              </div>
            </div>
          )}

          {activeTab === 'data' && (
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wide mb-3">
                  Végrehajtási adatok
                </h4>
                
                {node.data.executionData ? (
                  <div className={commonStyles.codeBlock}>
                    <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(node.data.executionData, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 mx-auto mb-2 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      <PlayIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Ez a node még nem futott le
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'info' && (
            <div className="space-y-3">
              <div className={commonStyles.card}>
                <h4 className="text-xs font-medium text-gray-900 dark:text-gray-100 uppercase tracking-wide mb-3">
                  Node információk
                </h4>
                
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">ID:</span>
                    <span className="font-mono text-gray-900 dark:text-gray-100">{node.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Típus:</span>
                    <span className="font-mono text-gray-900 dark:text-gray-100">{node.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Pozíció:</span>
                    <span className="font-mono text-gray-900 dark:text-gray-100">
                      {Math.round(node.position.x)}, {Math.round(node.position.y)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Inputok:</span>
                    <span className="text-gray-900 dark:text-gray-100">{node.data.inputs?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Outputok:</span>
                    <span className="text-gray-900 dark:text-gray-100">{node.data.outputs?.length || 0}</span>
                  </div>
                </div>
              </div>

              {/* Hibaüzenetek */}
              {node.data.hasError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                    <span className="text-xs font-medium text-red-700 dark:text-red-300">
                      Hiba történt
                    </span>
                  </div>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {node.data.errorMessage || 'Ismeretlen hiba'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Utolsó módosítás: most
          </div>
          
          {onDeleteNode && (
            <div className="flex items-center space-x-2">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center space-x-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                >
                  <TrashIcon className="w-3 h-3" />
                  <span>Törlés</span>
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Biztos?</span>
                  <button
                    onClick={handleDeleteNode}
                    className={commonStyles.dangerButton}
                  >
                    Igen
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Mégse
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NodePropertiesPanel;
